import { useChartSync } from './useChartSync';
import { useZoomState as _useZoomState } from './useZoomState';
import { ZoomProps, ChartZoomState } from '../../types';

type UseZoomProps<T> = Pick<
  ZoomProps,
  | 'useZoomState'
  | 'onResetZoom'
  | 'onZoom'
  | 'maxValue'
  | 'minValue'
  | 'xAxisDataKey'
> & {
  syncId?: string | number;
  chartData: T[];
};

export const useZoom = <T>({
  useZoomState,
  onResetZoom,
  onZoom,
  syncId,
  chartData,
  minValue,
  maxValue,
  xAxisDataKey,
}: UseZoomProps<T>): ChartZoomState => {
  const useStrategy = useZoomState
    ? useZoomState
    : _useZoomState.bind(null, {
        onZoom,
        onResetZoom,
      });

  const { hasZoomed, zoomedRange, persistZoomState, resetZoomState } =
    useStrategy();

  const {
    refAreaLeft,
    setRefAreaLeft,
    refAreaRight,
    setRefAreaRight,
    syncZoom,
  } = useChartSync({
    syncId,
    onSyncZoom: (from, to) => {
      if (from === null || to === null) {
        resetZoomState();
      } else {
        persistZoomState(from, to);
      }
    },
    minValue,
    maxValue,
    chartData,
    xAxisDataKey,
  });

  const zoom = () => {
    if (!refAreaLeft || !refAreaRight || refAreaLeft === refAreaRight) {
      setRefAreaLeft(null);
      setRefAreaRight(null);
      return;
    }

    // xAxis domain
    let [refAreaLeftTemp, refAreaRightTemp] = [refAreaLeft, refAreaRight];
    // Reversing the order if user is selecting from right to left
    if (refAreaLeft > refAreaRight) {
      [refAreaLeftTemp, refAreaRightTemp] = [refAreaRight, refAreaLeft];
    }

    // Sync the zoom state
    syncZoom(refAreaLeftTemp, refAreaRightTemp);

    // Reset the selection, for reference area to disappear
    setRefAreaLeft(null);
    setRefAreaRight(null);
  };

  const resetZoom = () => {
    resetZoomState();
    // Sync the zoom state with other charts
    syncZoom(null, null);
  };

  return {
    zoomedRange,
    zoom,
    refAreaLeft,
    setRefAreaLeft,
    refAreaRight,
    setRefAreaRight,
    hasZoomed,
    resetZoom,
  };
};
